import React, { useEffect, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";

import SnowScene from "./SnowScene";
import "./SnowDemo.css";

const SNOW_INFO = {
  eyebrow: "REAL-TIME GRAPHICS · DEFORMABLE SNOW",
  title: "GPU Deformable Snow",
  description:
    "An interactive snow surface represented by a GPU-updated height field. Moving objects deform the snow at runtime, leaving persistent tracks and depressions that are used to displace the rendered surface.",
  implementation: [
    "Deformation is stored in a 512 × 512 height map",
    "Ping-pong WebGL render targets accumulate deformation entirely on the GPU",
    "A fragment-shader feedback pass writes each new height-field state",
    "The resulting height map drives vertex displacement of a subdivided snow mesh",
    "Object radius and snow depth control the scale and depth of deformation",
    {
      text: "Inspired by the deformable-snow technique presented for Batman: Arkham Origins at GDC 2014",
      links: [
        {
          term: "Batman: Arkham Origins",
          url: "https://gdcvault.com/play/1020177/Deformable-Snow-Rendering-in-Batman",
        },
      ],
    },
  ],
  pipeline: [
    "Object Interaction",
    "Height-Map Feedback",
    "GPU Render Target",
    "Vertex Displacement",
    "Snow Surface",
  ],
};

export default function SnowDemo({ Panel, Slider, onInfoChange }) {
  const snowRef = useRef();

  const [ballSpeed, setBallSpeed] = useState(1.5);
  const [ballRadius, setBallRadius] = useState(0.65);
  const [snowDepth, setSnowDepth] = useState(0.45);
  const [ballDragging, setBallDragging] = useState(false);

  useEffect(() => {
    onInfoChange?.(SNOW_INFO);
  }, [onInfoChange]);

  return (
    <div className="reconstruction-workspace">
      <aside className="reconstruction-sidebar">
        <Panel title="SIMULATION">
          <button
            type="button"
            className="reconstruction-button"
            onClick={() => snowRef.current?.reset()}
          >
            RESET SNOW
          </button>
        </Panel>

        <Panel title="BALL">
          <Slider
            label="Speed"
            value={ballSpeed}
            min={0.25}
            max={4}
            step={0.05}
            display={ballSpeed.toFixed(2)}
            onChange={setBallSpeed}
          />

          <Slider
            label="Radius"
            value={ballRadius}
            min={0.3}
            max={1.2}
            step={0.05}
            display={ballRadius.toFixed(2)}
            onChange={setBallRadius}
          />
        </Panel>

        <Panel title="SNOW">
          <Slider
            label="Depth"
            value={snowDepth}
            min={0.1}
            max={1}
            step={0.01}
            display={snowDepth.toFixed(2)}
            onChange={setSnowDepth}
          />
        </Panel>
      </aside>

      <div className="reconstruction-viewer snow-viewer">
        <Canvas
          camera={{
            position: [0, 6, 8],
            fov: 45,
          }}
          gl={{ antialias: true }}
        >
          <color attach="background" args={["#181c1e"]} />

          <SnowScene
            ref={snowRef}
            ballSpeed={ballSpeed}
            ballRadius={ballRadius}
            snowDepth={snowDepth}
            onDraggingChange={setBallDragging}
          />

          <OrbitControls
            enabled={!ballDragging}
            target={[0, 0, 0]}
            minDistance={5}
            maxDistance={14}
            maxPolarAngle={Math.PI * 0.47}
          />
        </Canvas>

        <div className="viewer-overlay viewer-overlay-top">DEFORMABLE SNOW</div>

        <div className="viewer-overlay viewer-overlay-bottom">
          DRAG BALL: MOVE · DRAG BACKGROUND: ORBIT · SCROLL / PINCH: ZOOM · WASD
          / ARROWS: MOVE · R: RESET
        </div>
      </div>
    </div>
  );
}
